import { useState, useRef, useEffect, useMemo } from "react";
import "./OutputPanel.css";

const API_BASE_URL = window.location.origin;

function OutputPanel({ runConfig }) {
  const [callingStatus, setCallingStatus] = useState({});
  const [callIds, setCallIds] = useState({});
  const [conversationIds, setConversationIds] = useState({});
  const [isCallingAll, setIsCallingAll] = useState(false);

  const [viewMode, setViewMode] = useState("table");
  const [activeTranscript, setActiveTranscript] = useState(null);
  const [transcriptMessages, setTranscriptMessages] = useState([]);

  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  const [callSummaries, setCallSummaries] = useState({});

  const [onlyFinished, setOnlyFinished] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  const tableContainerRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const country = runConfig?.country ?? "";
  const fileName = runConfig?.fileName ?? "";
  const uploadResponse = runConfig?.uploadResponse ?? null;

  const rawRows = uploadResponse?.data ?? uploadResponse?.rows ?? [];
  const tableData = Array.isArray(rawRows) ? rawRows : [];

  const columns =
    uploadResponse?.columns ||
    (tableData[0] && typeof tableData[0] === "object"
      ? Object.keys(tableData[0])
      : []);

  const indexedRows = useMemo(() => {
    return tableData.map((r, i) => ({ r, i }));
  }, [tableData]);

  const filteredRows = useMemo(() => {
    if (!onlyFinished) return indexedRows;
    return indexedRows.filter(({ i }) => {
      const s = callingStatus[i];
      return s === "ended" || s === "no_answer";
    });
  }, [onlyFinished, indexedRows, callingStatus]);

  const counts = useMemo(() => {
    const total = tableData.length;
    const values = Object.values(callingStatus);

    const calling = values.filter((s) => s === "calling").length;
    const ongoing = values.filter((s) => s === "ongoing").length;
    const ended = values.filter((s) => s === "ended").length;
    const no_answer = values.filter((s) => s === "no_answer").length;

    const finished = ended + no_answer;
    const successRate = total > 0 ? (ended / total) * 100 : 0;

    return { total, calling, ongoing, ended, no_answer, finished, successRate };
  }, [callingStatus, tableData.length]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (viewMode === "transcript" && activeTranscript) {
      const index = activeTranscript.index;
      const callId = callIds[index];
      const status = callingStatus[index];

      if (callId && status && status !== "ended" && status !== "no_answer") {
        fetchTranscriptAndSummary(callId, index);

        pollIntervalRef.current = setInterval(() => {
          fetchTranscriptAndSummary(callId, index);
        }, 2000);
      } else if (callId) {
        fetchTranscriptAndSummary(callId, index);
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, activeTranscript, callIds, callingStatus]);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const fetchTranscriptAndSummary = async (callId, index) => {
    try {
      const transcriptResp = await fetch(
        `${API_BASE_URL}/api/call/transcript/${callId}`
      );
      const transcriptData = await transcriptResp.json();

      if (transcriptData.success) {
        if (transcriptData.status) {
          setCallingStatus((prev) => ({
            ...prev,
            [index]: transcriptData.status,
          }));
        }

        const transcript = transcriptData.transcript || [];
        if (transcript.length > 0) {
          const mapped = transcript.map((turn) => ({
            time:
              turn.time_in_call_secs != null
                ? `${Math.floor(turn.time_in_call_secs / 60)}:${String(
                    Math.floor(turn.time_in_call_secs % 60)
                  ).padStart(2, "0")}`
                : "00:00",
            speaker:
              turn.speaker || (turn.role === "user" ? "Customer" : "Sofia"),
            text: turn.text || "",
          }));
          setTranscriptMessages(mapped);
        }
      }

      const statusNow = callingStatus[index];
      if (statusNow === "ended" || statusNow === "no_answer") {
        const summaryResp = await fetch(
          `${API_BASE_URL}/api/call/summary/${callId}`
        );
        const summaryData = await summaryResp.json();

        if (summaryData.success && summaryData.summary) {
          setCallSummaries((prev) => ({ ...prev, [index]: summaryData.summary }));
        }
      }
    } catch (error) {
      console.error("Error fetching transcript/summary:", error);
    }
  };

  const getStatusClass = (status) =>
    status ? `status-${String(status).replace(/\s+/g, "-")}` : "";

  const getStatusLabel = (status) => {
    switch (status) {
      case "calling":
        return "Calling…";
      case "ongoing":
        return "Call in progress";
      case "ended":
        return "Call ended";
      case "no_answer":
        return "No answer";
      default:
        return "Initiating…";
    }
  };

  const transcriptStatus = activeTranscript
    ? callingStatus[activeTranscript.index]
    : null;

  const handleVerbose = async (record, index) => {
    setViewMode("transcript");
    setActiveTranscript({ record, index });
    setTranscriptMessages([]);

    const callId = callIds[index];
    if (!callId) {
      alert("Please start the call first for this row.");
      setViewMode("table");
      return;
    }

    await fetchTranscriptAndSummary(callId, index);
  };

  const handleCall = async (record, index, selectedCountry) => {
    setCallingStatus((prev) => ({ ...prev, [index]: "calling" }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/call/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...record, country: selectedCountry }),
      });

      const data = await response.json();

      if (data.success) {
        const callId = data.call_id;
        const convoId = data.conversation_id; 

        setCallIds((prev) => ({ ...prev, [index]: callId }));

        if (typeof convoId === "string" && convoId.startsWith("conv_")) {
          setConversationIds((prev) => ({ ...prev, [index]: convoId }));
        }

        pollCallStatus(callId, index);
      } else {
        throw new Error(data.error || "Failed to initiate call");
      }
    } catch (error) {
      console.error("Error initiating call:", error);
      setCallingStatus((prev) => ({ ...prev, [index]: "no_answer" }));
      alert(`Failed to initiate call: ${error.message || error}`);
    }
  };

  const pollCallStatus = (callId, index) => {
    if (!callId) return;

    const checkStatus = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/call/live-status/${callId}`
        );
        const data = await response.json();
        if (!data.success) return;

        const raw = data.app_status || "calling";

        let appStatus = "calling";
        if (
          raw === "calling" ||
          raw === "initiated" ||
          raw === "initiating" ||
          raw === "ringing"
        ) {
          appStatus = "calling";
        } else if (
          raw === "ongoing" ||
          raw === "in-progress" ||
          raw === "processing" ||
          raw === "accepted" ||
          raw === "connected"
        ) {
          appStatus = "ongoing";
        } else if (
          raw === "ended" ||
          raw === "completed" ||
          raw === "disconnected" ||
          raw === "done"
        ) {
          appStatus = "ended";
        } else if (
          raw === "no_answer" ||
          raw === "failed" ||
          raw === "busy" ||
          raw === "timeout"
        ) {
          appStatus = "no_answer";
        }

        setCallingStatus((prev) => ({ ...prev, [index]: appStatus }));

        if (appStatus === "calling" || appStatus === "ongoing") {
          setTimeout(checkStatus, 2000);
        } else {
          if (appStatus === "ended") fetchCallSummary(callId, index);
        }
      } catch (error) {
        console.error("Error checking live call status:", error);
      }
    };

    checkStatus();
  };

  const fetchCallSummary = async (callId, index) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/call/summary/${callId}`);
      const data = await response.json();

      if (data.success && data.summary) {
        setCallSummaries((prev) => ({ ...prev, [index]: data.summary }));
      }
    } catch (error) {
      console.error("Error fetching call summary:", error);
    }
  };

  const handleCallAll = async (rows, selectedCountry) => {
    setIsCallingAll(true);
    try {
      for (let j = 0; j < rows.length; j++) {
        await handleCall(rows[j], j, selectedCountry);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    } finally {
      setIsCallingAll(false);
    }
  };

  const handleGenerateReport = async (rows, selectedCountry, fileLabel) => {
    setReportError("");
    setReportLoading(true);

    try {
      const conversation_ids = tableData
          .map((_, index) => conversationIds[index])
          .filter((id) => typeof id === "string" && id.startsWith("conv_"));


      if (conversation_ids.length === 0) {
        alert(
          "No conversation IDs found. Please place calls first (or load existing convo ids)."
        );
        return;
      }

      const resp = await fetch(`${API_BASE_URL}/api/call-data/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversation_ids,
          context: { country: selectedCountry, fileName: fileLabel },
        }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(
          err?.error ? JSON.stringify(err.error) : `Report failed (${resp.status})`
        );
      }

      const blob = await resp.blob();
      const safeFile = (fileLabel || "calls").replace(/[^\w.-]+/g, "_");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      downloadBlob(blob, `sofia_report_${safeFile}_${ts}.xlsx`);
    } catch (e) {
      console.error("❌ Generate report failed:", e);
      setReportError(e?.message || String(e));
      alert(`Failed to generate report: ${e?.message || e}`);
    } finally {
      setReportLoading(false);
    }
  };

  const handleRowHover = (index, event) => {
    if (!callSummaries[index]) return;
    if (!tableContainerRef.current) return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    setHoveredRow(index);
    setHoverPosition({
      x: event.clientX - containerRect.left + 10,
      y: event.clientY - containerRect.top + 10,
    });
  };

  const handleRowMove = (index, event) => {
    if (!callSummaries[index]) return;
    if (!tableContainerRef.current) return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    setHoverPosition({
      x: event.clientX - containerRect.left + 10,
      y: event.clientY - containerRect.top + 10,
    });
  };

  const handleRowLeave = () => {
    setHoveredRow(null);
  };

  if (!runConfig) {
    return (
      <section className="panel auto-panel">
        <h2>Automatic output</h2>
        <p className="panel-subtitle">
          Configure the input and click <strong>Start</strong> to see results here.
        </p>
        <div className="auto-content">
          <p>Waiting for a run to start…</p>
        </div>
      </section>
    );
  }

  return (
    <section className="panel auto-panel">
      <div className="output-header">
        <div>
          <h2>Run summary</h2>
          <p className="panel-subtitle">
            {tableData.length > 0
              ? `Loaded ${tableData.length} records. Click the call button to initiate calls.`
              : "No data loaded yet. Upload a file and start the run."}
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <label style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={onlyFinished}
              onChange={(e) => setOnlyFinished(e.target.checked)}
            />
            <span style={{ color: "rgba(245,245,247,0.85)", fontSize: 13 }}>
              Show only finished
            </span>
          </label>
        </div>
      </div>

      <div className="auto-content">
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            marginBottom: "14px",
          }}
        >
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(18,18,22,0.45)",
              color: "rgba(245,245,247,0.9)",
              fontSize: 13,
            }}
          >
            🌍 <strong>Country:</strong> {country}
          </div>

          <div
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(18,18,22,0.45)",
              color: "rgba(245,245,247,0.9)",
              fontSize: 13,
            }}
          >
            📄 <strong>File:</strong> {fileName || "—"}
          </div>

          <div
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(18,18,22,0.45)",
              color: "rgba(245,245,247,0.9)",
              fontSize: 13,
            }}
          >
            ✅ <strong>Ended:</strong> {counts.ended} / {counts.total} (
            {counts.successRate.toFixed(1)}%)
          </div>

          <div
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(18,18,22,0.45)",
              color: "rgba(245,245,247,0.9)",
              fontSize: 13,
            }}
          >
            ⛔ <strong>No answer:</strong> {counts.no_answer}
          </div>

          <div
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(18,18,22,0.45)",
              color: "rgba(245,245,247,0.9)",
              fontSize: 13,
            }}
          >
            📞 <strong>In progress:</strong> {counts.calling + counts.ongoing}
          </div>
        </div>

        {tableData.length > 0 && (
          <div style={{ marginTop: "16px" }}>
            <div className="table-header">
              {viewMode === "transcript" ? (
                <button
                  className="back-btn"
                  onClick={() => {
                    setViewMode("table");
                    setActiveTranscript(null);
                    setTranscriptMessages([]);
                  }}
                >
                  ← Back to Table
                </button>
              ) : (
                <>
                  <h3 style={{ margin: 0 }}>Call Records</h3>
                  <div className="action-buttons">
                    <button
                      className="call-all-btn"
                      onClick={() => handleCallAll(tableData, country)}
                      disabled={isCallingAll}
                      title="Initiate calls for all rows"
                    >
                      {isCallingAll ? "Calling..." : "📞 Call All"}
                    </button>

                    <button
                      className="report-btn"
                      onClick={() => handleGenerateReport(tableData, country, fileName)}
                      disabled={reportLoading}
                      title="Download an Excel report for calls that have conversation ids"
                    >
                      {reportLoading ? "Generating…" : "📊 Generate Report"}
                    </button>
                  </div>
                </>
              )}
            </div>

            {reportError ? (
              <div
                style={{
                  marginBottom: 12,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(239,68,68,0.35)",
                  background: "rgba(239,68,68,0.08)",
                  color: "rgba(252,165,165,0.95)",
                  fontSize: 13,
                }}
              >
                {reportError}
              </div>
            ) : null}

            {viewMode === "table" ? (
              <div
                className="table-container"
                ref={tableContainerRef}
                onMouseLeave={handleRowLeave}
              >
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="call-column">Action</th>
                      <th className="verbose-column">Details</th>
                      {columns.map((col) => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map(({ r: record, i: index }) => {
                      const status = callingStatus[index];

                      return (
                        <tr
                          key={index}
                          className={`call-row ${getStatusClass(status)}`}
                          onMouseEnter={(e) => handleRowHover(index, e)}
                          onMouseMove={(e) => handleRowMove(index, e)}
                        >
                          <td className="call-column">
                            <button
                              className={`call-btn ${getStatusClass(status)}`}
                              onClick={() => handleCall(record, index, country)}
                              disabled={status === "calling" || status === "ongoing"}
                              title="Start call"
                            >
                              {status === "calling" && "📞"}
                              {status === "ongoing" && "🔊"}
                              {status === "ended" && "✓"}
                              {status === "no_answer" && "⏹"}
                              {!status && "📞"}
                            </button>
                          </td>

                          <td className="verbose-column">
                            <button
                              className="verbose-btn"
                              onClick={() => handleVerbose(record, index)}
                              title="View call transcript"
                            >
                              📝
                            </button>
                          </td>

                          {columns.map((col) => (
                            <td key={col} title={String(record?.[col] ?? "")}>
                              {record?.[col] ?? "—"}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {hoveredRow !== null && callSummaries[hoveredRow] && (
                  <div
                    className="call-summary-tooltip"
                    style={{
                      left: `${hoverPosition.x}px`,
                      top: `${hoverPosition.y}px`,
                    }}
                  >
                    <h4>Call Summary</h4>
                    <p>
                      <strong>Status:</strong> {callSummaries[hoveredRow].status}
                    </p>
                    <p>
                      <strong>Duration:</strong> {callSummaries[hoveredRow].duration}s
                    </p>
                    <p>
                      <strong>Outcome:</strong> {callSummaries[hoveredRow].outcome}
                    </p>
                    <p>
                      <strong>Next Action:</strong>{" "}
                      {callSummaries[hoveredRow].next_action || "N/A"}
                    </p>
                    <p className="summary-notes">{callSummaries[hoveredRow].notes}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="transcript-container">
                <div className="transcript-header">
                  <div>
                    <h3>
                      Call Transcript - {activeTranscript?.record?.DEBTOR_NAME || "Unknown"}
                    </h3>
                    <p>
                      Account: {activeTranscript?.record?.CEID} | Phone:{" "}
                      {activeTranscript?.record?.MAIN_PHONE_NUMBER}
                    </p>
                  </div>

                  <div className={`call-status-large ${getStatusClass(transcriptStatus)}`}>
                    <span className="status-dot" />
                    <span className="status-text">{getStatusLabel(transcriptStatus)}</span>
                  </div>

                  {callSummaries[activeTranscript?.index] && (
                    <div className="transcript-summary">
                      <h4>Call Summary</h4>
                      <p>
                        <strong>Outcome:</strong>{" "}
                        {callSummaries[activeTranscript.index].outcome}
                      </p>
                      <p>
                        <strong>Duration:</strong>{" "}
                        {callSummaries[activeTranscript.index].duration}s
                      </p>
                      {callSummaries[activeTranscript.index].title && (
                        <p>
                          <strong>Title:</strong>{" "}
                          {callSummaries[activeTranscript.index].title}
                        </p>
                      )}
                      <p>
                        <strong>Notes:</strong>{" "}
                        {callSummaries[activeTranscript.index].notes}
                      </p>
                    </div>
                  )}
                </div>

                <div className="transcript-messages">
                  {transcriptMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`transcript-message ${String(msg.speaker)
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      <div className="message-time">{msg.time}</div>
                      <div className="message-content">
                        <strong>{msg.speaker}:</strong> {msg.text}
                      </div>
                    </div>
                  ))}

                  {transcriptMessages.length === 0 && (
                    <div className="transcript-loading">
                      <p>
                        {transcriptStatus === "calling" || transcriptStatus === "ongoing"
                          ? "⏳ Call in progress... Transcript will appear here automatically."
                          : "⏳ Loading transcript..."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default OutputPanel;
